import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import opoLogo from "../assets/opo-logo.png";
import { vps } from "../api/endpoints";
import { useAuth } from "../auth/AuthContext";
import { applyTheme, readStoredTheme, type ThemeName } from "../lib/theme";
import { Icon, type IconName } from "./Icon";
import { Button, cx } from "./ui";

const SIDEBAR_COLLAPSED_KEY = "backoffice.sidebarCollapsed";

/** Same destinations, same order, as the desktop sidebar's NAV_ITEMS. */
const NAV_ITEMS: { to: string; label: string; icon: IconName }[] = [
  { to: "/dashboard", label: "Dashboard", icon: "dashboard" },
  { to: "/records", label: "Records", icon: "records" },
  { to: "/bulk-paste", label: "Bulk Paste", icon: "paste" },
  { to: "/statistics", label: "Statistics", icon: "stats" },
  { to: "/vps", label: "VPS Tracker", icon: "server" },
  { to: "/database", label: "Database", icon: "database" },
  { to: "/settings", label: "Settings", icon: "settings" },
];

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  // The 30-day VPS reminder is the tracker's whole point, so the nav item
  // itself carries the due count — visible from any page, refreshed quietly.
  const dueQuery = useQuery({
    queryKey: ["vps", "due"],
    queryFn: () => vps.due(),
    refetchInterval: 5 * 60_000,
  });
  const dueCount = dueQuery.data?.length ?? 0;

  return (
    <nav className="flex flex-col gap-1">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cx(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium",
              isActive ? "nav-item-active" : "nav-item-inactive",
            )
          }
        >
          <Icon name={item.icon} className="size-[18px] shrink-0" />
          {item.label}
          {item.to === "/vps" && dueCount > 0 ? (
            <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-warning/20 px-1.5 py-0.5 text-xs font-semibold text-warning">
              {dueCount}
            </span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}

function ThemeToggle({ theme, onToggle }: { theme: ThemeName; onToggle: () => void }) {
  return (
    <Button variant="ghost" onClick={onToggle} className="w-full justify-start">
      <Icon name={theme === "dark" ? "sun" : "moon"} className="size-[18px]" />
      {theme === "dark" ? "Light Mode" : "Dark Mode"}
    </Button>
  );
}

export function Layout() {
  const { operator, signOut } = useAuth();
  const [theme, setTheme] = useState<ThemeName>(() => readStoredTheme());
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Desktop-only: hides the rail entirely for more page width. A separate
  // concern from the mobile drawer above, which always overlays instead.
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1",
  );
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => applyTheme(theme), [theme]);

  // A route change from anywhere (including the browser's back button) should
  // leave the drawer closed rather than covering the page it navigated to.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // The drawer is an overlay; letting the page behind it scroll is disorienting
  // on touch devices.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const toggleTheme = () => setTheme((current) => (current === "dark" ? "light" : "dark"));

  const sidebarBody = (
    <>
      <div className="mb-5 flex items-center gap-2 px-1">
        <img src={opoLogo} alt="OPO" className="h-4 w-auto shrink-0" />
        <div>
          <p className="text-sm font-semibold tracking-tight text-fg">BackOffice</p>
          <p className="text-xs text-fg-muted">OPO</p>
        </div>
      </div>
      <NavItems onNavigate={() => setDrawerOpen(false)} />
      <div className="mt-auto flex flex-col gap-1 border-t border-line pt-3">
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        <div className="px-3 pt-1 pb-2">
          <p className="truncate text-xs font-medium text-fg">{operator?.name}</p>
          <p className="truncate text-xs text-fg-muted">{operator?.email}</p>
        </div>
        <Button variant="ghost" onClick={() => void signOut()} className="w-full justify-start">
          <Icon name="logout" className="size-[18px]" />
          Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-full">
      {/* Permanent rail from `lg` up, unless collapsed; drawer below that. */}
      {!collapsed ? (
        <aside className="sidebar-shell relative hidden w-60 shrink-0 flex-col border-r border-line p-3 lg:flex">
          <button
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            className="absolute top-4 -right-3 z-10 flex size-6 cursor-pointer items-center justify-center rounded-full border border-line bg-elevated text-fg-muted shadow-[var(--shadow-sm)] hover:bg-hover hover:text-fg"
          >
            <Icon name="chevron-left" className="size-3.5" />
          </button>
          {sidebarBody}
        </aside>
      ) : (
        <button
          onClick={() => setCollapsed(false)}
          aria-label="Expand sidebar"
          title="Expand sidebar"
          className="fixed top-1/2 left-0 z-30 hidden -translate-y-1/2 cursor-pointer items-center justify-center rounded-r-lg border border-l-0 border-line bg-elevated px-1.5 py-3 text-fg-muted shadow-[var(--shadow-md)] hover:bg-hover hover:text-fg lg:flex"
        >
          <Icon name="chevron-right" className="size-4" />
        </button>
      )}

      {drawerOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/60"
          />
          <aside className="sidebar-shell relative flex h-full w-[17rem] max-w-[85vw] flex-col border-r border-line p-3">
            {sidebarBody}
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-2 border-b border-line bg-sidebar px-3 py-2 lg:hidden">
          <Button
            variant="ghost"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="px-2"
          >
            <Icon name="menu" className="size-5" />
          </Button>
          <img src={opoLogo} alt="OPO" className="h-4 w-auto" />
          <span className="text-sm font-semibold">BackOffice</span>
          <div className="ml-auto">
            <Button variant="ghost" onClick={toggleTheme} aria-label="Toggle theme" className="px-2">
              <Icon name={theme === "dark" ? "sun" : "moon"} className="size-5" />
            </Button>
          </div>
        </header>

        <main className="app-bg min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
